import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type RigaScadenzario } from '../api';
import {
  Caricamento,
  Campo,
  Errore,
  Etichetta,
  Importo,
  LinkFattura,
  Scheda,
  Vuoto,
  useCarico,
  useDebounce,
  useFiltriUrl,
} from '../componenti/base';
import { etichettaMese, etichettaStato, formattaData, formattaEuro, meseDi, plurale } from '../formato';
import { useSessione } from '../main';

const ORIZZONTI = [
  { v: 30, t: 'Prossimi 30 giorni' },
  { v: 60, t: 'Prossimi 60 giorni' },
  { v: 90, t: 'Prossimi 90 giorni' },
  { v: 180, t: 'Prossimi 6 mesi' },
  { v: 365, t: 'Prossimi 12 mesi' },
];

interface Gruppo {
  chiave: string;
  titolo: string;
  scaduto: boolean;
  righe: RigaScadenzario[];
  incassi: number;
  uscite: number;
}

/**
 * Le scadute vanno in un gruppo a parte in cima (indipendente dal mese): in
 * tesoreria è la prima cosa da guardare. Il resto è raggruppato per mese di
 * cassa attesa — non per scadenza nominale, perché è la data attesa che muove
 * la cassa.
 */
function raggruppa(righe: RigaScadenzario[]): Gruppo[] {
  const scadute = righe.filter((r) => r.statoEsteso === 'SCADUTA');
  const future = righe.filter((r) => r.statoEsteso !== 'SCADUTA');

  const perMese = new Map<string, RigaScadenzario[]>();
  for (const r of future) {
    const m = meseDi(r.dataCassaAttesa);
    (perMese.get(m) ?? perMese.set(m, []).get(m)!).push(r);
  }

  const somma = (rr: RigaScadenzario[], tipo: 'ATTIVA' | 'PASSIVA') =>
    rr.filter((r) => r.tipo === tipo).reduce((t, r) => t + r.importoCent, 0);

  const gruppi: Gruppo[] = [];
  if (scadute.length) {
    gruppi.push({
      chiave: 'scadute',
      titolo: 'Scadute',
      scaduto: true,
      righe: scadute,
      incassi: somma(scadute, 'ATTIVA'),
      uscite: somma(scadute, 'PASSIVA'),
    });
  }
  for (const m of [...perMese.keys()].sort()) {
    const rr = perMese.get(m)!;
    gruppi.push({
      chiave: m,
      titolo: etichettaMese(m, { lungo: true }),
      scaduto: false,
      righe: rr,
      incassi: somma(rr, 'ATTIVA'),
      uscite: somma(rr, 'PASSIVA'),
    });
  }
  return gruppi;
}

const MAX_PER_GRUPPO = 25;

interface F24InScadenza {
  id: string;
  descrizione: string;
  dataScadenza: string;
  saldoCent: number;
}

export function Scadenzario() {
  const { organizzazione } = useSessione();
  // Nell'indirizzo: dallo scadenzario si entra nelle fatture di continuo, e
  // tornare indietro non deve far ricominciare da capo. `giorni` compreso: è
  // la finestra che si stava guardando, non una preferenza.
  const { filtri, imposta, azzera } = useFiltriUrl({
    giorni: '60',
    tipo: '',
    soloScadute: '',
    cerca: '',
  });
  const giorni = Number(filtri.giorni) || 60;
  const tipo = filtri.tipo as '' | 'ATTIVA' | 'PASSIVA';
  const soloScadute = filtri.soloScadute === 'si';
  const cerca = filtri.cerca;
  const cercaRitardata = useDebounce(cerca);
  /**
   * I gruppi mostrati per intero. Gli altri si fermano a `MAX_PER_GRUPPO`: con
   * quattrocento righe in pagina si scorre a vuoto, e la scadenza che si stava
   * cercando è comunque in cima al suo mese, perché l'ordine è per data.
   */
  const [tutti, setTutti] = useState<Set<string>>(new Set());
  const f24 = useCarico(() => api.get<F24InScadenza[]>('/api/f24?stato=DA_VERSARE'), []);

  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<RigaScadenzario[]>(`/api/scadenzario?giorni=${giorni}`),
    [giorni],
  );

  const righe = useMemo(() => dati ?? [], [dati]);

  // KPI sull'intera finestra caricata: danno il quadro, non seguono i filtri di
  // tabella (che servono a restringere la lista, non a cambiare i totali).
  const kpi = useMemo(() => {
    let incassi = 0;
    let uscite = 0;
    let scaduto = 0;
    let nScadute = 0;
    for (const r of righe) {
      if (r.tipo === 'ATTIVA') incassi += r.importoCent;
      else uscite += r.importoCent;
      if (r.statoEsteso === 'SCADUTA') {
        scaduto += r.importoCent;
        nScadute += 1;
      }
    }
    return { incassi, uscite, scaduto, nScadute };
  }, [righe]);

  const filtrate = useMemo(() => {
    const q = cercaRitardata.trim().toLowerCase();
    return righe.filter((r) => {
      if (tipo && r.tipo !== tipo) return false;
      if (soloScadute && r.statoEsteso !== 'SCADUTA') return false;
      if (q && !`${r.numero} ${r.controparte ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [righe, tipo, soloScadute, cercaRitardata]);

  const gruppi = useMemo(() => raggruppa(filtrate), [filtrate]);

  const oggiIso = new Date().toISOString().slice(0, 10);
  const f24Aperti = useMemo(
    () => [...(f24.dati ?? [])].sort((a, b) => a.dataScadenza.localeCompare(b.dataScadenza)),
    [f24.dati],
  );
  const f24Totale = f24Aperti.reduce((t, f) => t + f.saldoCent, 0);
  const f24Scaduti = f24Aperti
    .filter((f) => f.dataScadenza < oggiIso)
    .reduce((a, f) => ({ quanti: a.quanti + 1, importoCent: a.importoCent + f.saldoCent }), {
      quanti: 0,
      importoCent: 0,
    });
  // I primi tre: la scheda è un richiamo, non un elenco. L'elenco sta in F24.
  const f24Prossimi = f24Aperti.slice(0, 3);
  const filtriAttivi = tipo !== '' || soloScadute || cerca !== '';

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Scadenzario</h1>
        <p className="text-sm text-inchiostro-500">
          Fatture non saldate, ordinate per data di cassa attesa — la scadenza corretta col ritardo medio storico della
          controparte, non quella nominale.
        </p>
      </header>

      {/* Quadro d'insieme sulla finestra scelta. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="scheda border-t-incassi p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">Da incassare</p>
          <p className="mt-1 text-2xl">
            <Importo cent={kpi.incassi} tono="incassi" grassetto />
          </p>
          <p className="mt-1 text-xs text-inchiostro-500">fatture attive nella finestra</p>
        </div>
        <div className="scheda border-t-uscite p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">Da pagare</p>
          <p className="mt-1 text-2xl">
            <Importo cent={kpi.uscite} tono="uscite" grassetto />
          </p>
          <p className="mt-1 text-xs text-inchiostro-500">fatture passive nella finestra</p>
        </div>
        <div className={`scheda p-4 ${kpi.nScadute ? 'border-t-allerta' : 'border-t-inchiostro-300'}`}>
          <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">Scadute</p>
          <p className={`num mt-1 text-2xl font-semibold ${kpi.nScadute ? 'text-allerta' : 'text-inchiostro-800'}`}>
            {formattaEuro(kpi.scaduto)}
          </p>
          <p className="mt-1 text-xs text-inchiostro-500">
            {kpi.nScadute === 0
              ? 'niente oltre la data attesa'
              : `${kpi.nScadute} document${kpi.nScadute === 1 ? 'o' : 'i'} oltre la data attesa`}
          </p>
        </div>
        {/* Gli F24 non sono fatture e non stanno nella tabella qui sotto, ma
            sono uscite con una scadenza: tenerli fuori da un elenco di scadenze
            vorrebbe dire far preparare i soldi per le fatture e scoprire il
            versamento il giorno dell'addebito. */}
        <div className={`scheda p-4 ${f24Scaduti.quanti ? 'border-t-allerta' : 'border-t-attenzione'}`}>
          <p className="text-xs font-medium uppercase tracking-wide text-inchiostro-500">F24 da versare</p>
          <p className="mt-1 text-2xl">
            <Importo cent={f24Totale} tono="uscite" grassetto />
          </p>
          <p className="mt-1 text-xs text-inchiostro-500">
            {f24Aperti.length === 0 ? (
              'nessun modulo registrato'
            ) : (
              <>
                {plurale(f24Aperti.length, 'modulo', 'moduli')}
                {f24Scaduti.quanti > 0 && (
                  <>
                    , di cui{' '}
                    <span className="font-medium text-allerta">
                      {f24Scaduti.quanti} {f24Scaduti.quanti === 1 ? 'scaduto' : 'scaduti'} per{' '}
                      {formattaEuro(f24Scaduti.importoCent)}
                    </span>
                  </>
                )}
              </>
            )}
          </p>
          {f24Prossimi.length > 0 && (
            <ul className="mt-2 space-y-0.5 border-t border-inchiostro-200 pt-2 text-xs">
              {f24Prossimi.map((f) => (
                <li key={f.id} className="flex items-baseline justify-between gap-2">
                  <Link to="/f24" className="truncate underline decoration-dotted underline-offset-2">
                    {f.descrizione}
                  </Link>
                  <span className="whitespace-nowrap text-inchiostro-500">
                    <span className={f.dataScadenza < oggiIso ? 'text-allerta' : ''}>
                      {formattaData(f.dataScadenza, organizzazione.formatoData)}
                    </span>{' '}
                    · <span className="num">{formattaEuro(f.saldoCent)}</span>
                  </span>
                </li>
              ))}
              {f24Aperti.length > f24Prossimi.length && (
                <li className="pt-0.5 text-inchiostro-500">
                  <Link to="/f24" className="underline underline-offset-2">
                    e altri {f24Aperti.length - f24Prossimi.length}
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <Scheda senzaPadding>
        <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 p-3">
          <Campo etichetta="Orizzonte" larghezza="w-48">
            <select className="campo" value={giorni} onChange={(e) => imposta({ giorni: e.target.value })}>
              {ORIZZONTI.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.t}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Tipo" larghezza="w-44">
            <select className="campo" value={tipo} onChange={(e) => imposta({ tipo: e.target.value })}>
              <option value="">Attive e passive</option>
              <option value="ATTIVA">Solo da incassare</option>
              <option value="PASSIVA">Solo da pagare</option>
            </select>
          </Campo>
          <Campo etichetta="Cerca" larghezza="w-60">
            <input
              className="campo"
              placeholder="Numero o controparte"
              value={cerca}
              onChange={(e) => imposta({ cerca: e.target.value })}
            />
          </Campo>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={soloScadute}
              onChange={(e) => imposta({ soloScadute: e.target.checked ? 'si' : '' })}
            />
            Solo scadute
          </label>
          {filtriAttivi && (
            <button className="bottone-secondario" onClick={azzera}>
              Azzera filtri
            </button>
          )}
        </div>

        {caricamento ? (
          <Caricamento />
        ) : errore ? (
          <div className="p-4">
            <Errore testo={errore} riprova={ricarica} />
          </div>
        ) : gruppi.length === 0 ? (
          <Vuoto
            titolo={filtriAttivi ? 'Nessuna scadenza con questi filtri' : 'Nessuna scadenza nella finestra'}
            testo={
              filtriAttivi
                ? 'Prova ad allargare l’orizzonte o ad azzerare i filtri.'
                : 'Tutto ciò che era da saldare risulta già saldato in questo periodo.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Cassa attesa</th>
                  <th>Documento</th>
                  <th>Controparte</th>
                  <th>Stato</th>
                  <th className="text-right">Importo</th>
                </tr>
              </thead>
              {gruppi.map((g) => {
                // Il gruppo si ferma a venticinque righe, ma i **totali**
                // dell'intestazione restano quelli di tutte: un totale che
                // segue il taglio direbbe che il mese pesa meno di quanto pesa.
                const intero = tutti.has(g.chiave);
                const mostrate = intero ? g.righe : g.righe.slice(0, MAX_PER_GRUPPO);
                const nascoste = g.righe.length - mostrate.length;
                return (
                <tbody key={g.chiave}>
                  <tr className={g.scaduto ? 'bg-allerta-chiaro/60' : 'bg-inchiostro-50'}>
                    <th colSpan={3} className="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-inchiostro-600">
                      {g.scaduto ? (
                        <span className="text-allerta">{g.titolo}</span>
                      ) : (
                        g.titolo
                      )}
                    </th>
                    <td colSpan={2} className="px-3 py-1.5 text-right text-xs text-inchiostro-600">
                      {g.incassi > 0 && (
                        <span className="mr-3">
                          + <Importo cent={g.incassi} tono="incassi" />
                        </span>
                      )}
                      {g.uscite > 0 && (
                        <span>
                          − <Importo cent={g.uscite} tono="uscite" />
                        </span>
                      )}
                    </td>
                  </tr>
                  {mostrate.map((r, i) => (
                    <tr key={`${r.documentoId}-${i}`}>
                      <td className="num whitespace-nowrap">
                        {formattaData(r.dataCassaAttesa, organizzazione.formatoData)}
                        {r.dataScadenza !== r.dataCassaAttesa && (
                          <span
                            className="ml-1 text-xs text-inchiostro-400"
                            title={`Scadenza nominale: ${formattaData(r.dataScadenza, organizzazione.formatoData)}`}
                          >
                            (nom. {formattaData(r.dataScadenza, organizzazione.formatoData)})
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        <LinkFattura id={r.documentoId} className="font-medium underline underline-offset-2">
                          {r.numero}
                        </LinkFattura>
                        <span className="ml-2 text-xs text-inchiostro-500">{r.tipo === 'ATTIVA' ? 'attiva' : 'passiva'}</span>
                      </td>
                      <td className="max-w-56 truncate">{r.controparte ?? '—'}</td>
                      <td>
                        <StatoScadenza r={r} />
                      </td>
                      <td className="text-right">
                        <Importo cent={r.importoCent} tono={r.tipo === 'ATTIVA' ? 'incassi' : 'uscite'} />
                      </td>
                    </tr>
                  ))}
                  {(nascoste > 0 || intero) && (
                    <tr>
                      <td colSpan={5} className="px-3 py-1.5 text-center text-xs">
                        <button
                          className="underline underline-offset-2 text-inchiostro-600 hover:text-inchiostro-900"
                          onClick={() =>
                            setTutti((v) => {
                              const n = new Set(v);
                              if (n.has(g.chiave)) n.delete(g.chiave);
                              else n.add(g.chiave);
                              return n;
                            })
                          }
                        >
                          {intero
                            ? `Mostra solo le prime ${MAX_PER_GRUPPO} di ${g.righe.length}`
                            : `Mostra le altre ${nascoste} · ${g.righe.length} in tutto`}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
                );
              })}
            </table>
          </div>
        )}
      </Scheda>
    </div>
  );
}

function StatoScadenza({ r }: { r: RigaScadenzario }) {
  const s = r.statoEsteso;
  const tono = s === 'SCADUTA' ? 'allerta' : s === 'PARZIALE' ? 'attenzione' : 'neutro';
  return (
    <div className="flex flex-col items-start gap-1">
      <Etichetta testo={etichettaStato(s, r.tipo)} tono={tono} />
      {/* Un documento parziale è in parte già saldato: si dichiara il residuo,
          così l'importo della rata non si legge come intero ancora dovuto. */}
      {r.stato === 'PARZIALE' && (
        <span className="text-xs text-inchiostro-500">residuo doc. {formattaEuro(r.residuoDocumentoCent)}</span>
      )}
    </div>
  );
}
